import React, { useState, useEffect, useCallback } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';
import { getSalesHeaders as getHeaders } from '../../utils/salesAuth';
import { timeAgo, fmtFull as formatDate, fmtDate } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmailView() {
  const [tab, setTab] = useState('inbox');
  const [emails, setEmails] = useState([]);
  const [inboxEmails, setInboxEmails] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCompose, setShowCompose] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [thread, setThread] = useState([]);
  const isMobile = useIsMobile();

  const fetchEmails = useCallback(async (folder) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/emails?folder=${folder}&limit=200`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) setEmails(await res.json());
    } finally { setLoading(false); }
  }, []);

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/inbox`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) setInboxEmails(await res.json());
    } finally { setLoading(false); }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/sales/inbox/unread-count`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) { const d = await res.json(); setUnreadCount(d.count); }
    } catch {}
  }, []);

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/emails/drafts`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) setDrafts(await res.json());
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (tab === 'drafts') fetchDrafts();
    else if (tab === 'inbox') fetchInbox();
    else fetchEmails(tab);
    fetchUnreadCount();
  }, [tab, fetchEmails, fetchDrafts, fetchInbox, fetchUnreadCount]);

  const handleSent = () => {
    setShowCompose(false);
    setEditingDraft(null);
    setReplyTo(null);
    setTab('sent');
    fetchEmails('sent');
  };

  const handleDraftSaved = () => {
    setShowCompose(false);
    setEditingDraft(null);
    setReplyTo(null);
    setTab('drafts');
    fetchDrafts();
  };

  const handleReply = (email) => {
    setReplyTo(email);
    setShowCompose(true);
    setSelected(null);
  };

  const loadThread = async (emailId) => {
    try {
      const res = await fetch(`${API}/api/sales/inbox/${emailId}/thread`, { credentials: 'include', headers: getHeaders() });
      if (res.ok) setThread(await res.json());
    } catch {}
  };

  const selectInboxEmail = async (email) => {
    setSelected(email);
    // Mark as read
    if (!email.is_read) {
      await fetch(`${API}/api/sales/inbox/${email.id}/read`, {
        method: 'POST', credentials: 'include', headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_read: true }),
      });
      setInboxEmails(prev => prev.map(e => e.id === email.id ? { ...e, is_read: true } : e));
      fetchUnreadCount();
    }
    loadThread(email.id);
  };

  const openDraft = (draft) => {
    setEditingDraft(draft);
    setShowCompose(true);
    setSelected(null);
  };

  const deleteDraft = async (draftId) => {
    const res = await fetch(`${API}/api/sales/emails/drafts/${draftId}`, { method: 'DELETE', credentials: 'include', headers: getHeaders() });
    if (res.ok) setDrafts(prev => prev.filter(d => d.id !== draftId));
  };

  return (
    <div data-testid="email-view" style={{ padding: isMobile ? '16px 12px' : '24px 28px', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>Email</h1>
        <button data-testid="compose-email-btn" onClick={() => { setShowCompose(true); setSelected(null); setEditingDraft(null); }}
          style={{ padding: '8px 18px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          Compose
        </button>
      </div>

      {showCompose ? (
        <ComposeEmail onSent={handleSent} onCancel={() => { setShowCompose(false); setEditingDraft(null); setReplyTo(null); }} onDraftSaved={handleDraftSaved} editingDraft={editingDraft} replyTo={replyTo} />
      ) : selected ? (
        tab === 'inbox' ? (
          <InboxEmailDetail email={selected} thread={thread} onBack={() => { setSelected(null); setThread([]); }} onReply={handleReply} isMobile={isMobile} />
        ) : (
          <EmailDetail email={selected} onBack={() => setSelected(null)} />
        )
      ) : (
        <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { key: 'inbox', label: 'Inbox' },
              { key: 'sent', label: 'Sent' },
              { key: 'drafts', label: 'Drafts' },
              { key: 'all', label: 'All' },
            ].map(f => (
              <button key={f.key} data-testid={`email-tab-${f.key}`} onClick={() => { setTab(f.key); setSelected(null); }}
                style={{ padding: '6px 16px', borderRadius: 6, border: tab === f.key ? 'none' : '1px solid #d1d5db',
                  background: tab === f.key ? '#1e293b' : '#fff', color: tab === f.key ? '#fff' : '#64748b',
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, minHeight: 36 }}>
                {f.label}
                {f.key === 'inbox' && unreadCount > 0 && (
                  <span style={{ background: '#c0392b', color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 10, fontWeight: 700 }}>{unreadCount}</span>
                )}
                {f.key === 'drafts' && drafts.length > 0 && tab !== 'drafts' && (
                  <span style={{ background: '#94a3b8', color: '#fff', borderRadius: 8, padding: '0 5px', fontSize: 10, fontWeight: 700 }}>{drafts.length}</span>
                )}
              </button>
            ))}
            <button data-testid="email-refresh-btn" onClick={() => {
              if (tab === 'drafts') fetchDrafts();
              else if (tab === 'inbox') fetchInbox();
              else fetchEmails(tab);
              fetchUnreadCount();
            }}
              style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#64748b',
                fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, minHeight: 36, marginLeft: 4 }}
              title="Refresh">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2v6h-6"/><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M3 22v-6h6"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/></svg>
              {!isMobile && 'Refresh'}
            </button>
          </div>

          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Loading...</div>
          ) : tab === 'inbox' ? (
            inboxEmails.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
                <p style={{ fontSize: 14, marginBottom: 8 }}>Inbox is empty</p>
                <p style={{ fontSize: 12 }}>Inbound emails will appear here when configured</p>
              </div>
            ) : (
              <InboxList emails={inboxEmails} onSelect={selectInboxEmail} isMobile={isMobile} />
            )
          ) : tab === 'drafts' ? (
            <DraftsList drafts={drafts} onOpen={openDraft} onDelete={deleteDraft} />
          ) : emails.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <p style={{ fontSize: 14, marginBottom: 8 }}>No emails yet</p>
              <p style={{ fontSize: 12 }}>Click <strong>Compose</strong> to send your first email</p>
            </div>
          ) : (
            <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
              {emails.map(e => (
                <div key={e.id} data-testid={`email-row-${e.id}`} onClick={() => setSelected(e)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f1f5f9',
                    cursor: 'pointer', transition: 'background .1s' }}
                  onMouseOver={ev => ev.currentTarget.style.background = '#f8fafc'}
                  onMouseOut={ev => ev.currentTarget.style.background = 'transparent'}>
                  <div style={{ width: 32, height: 32, borderRadius: '50%', background: e.status === 'sent' ? '#dcfce7' : e.status === 'failed' ? '#fef2f2' : '#f1f5f9',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                      stroke={e.status === 'sent' ? '#16a34a' : e.status === 'failed' ? '#dc2626' : '#64748b'}
                      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      {e.status === 'sent' ? <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/> : <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zM22 6l-10 7L2 6"/>}
                    </svg>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.customer_name || e.to_email}
                      </span>
                      <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, marginLeft: 8 }}>{timeAgo(e.created_at)}</span>
                    </div>
                    <div style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {e.subject || '(no subject)'}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}>
                      <span style={{ fontSize: 10, color: '#94a3b8' }}>To: {e.to_email}</span>
                      <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, fontWeight: 600,
                        background: e.status === 'sent' ? '#dcfce7' : e.status === 'failed' ? '#fef2f2' : '#fef9c3',
                        color: e.status === 'sent' ? '#16a34a' : e.status === 'failed' ? '#dc2626' : '#ca8a04',
                        textTransform: 'uppercase' }}>{e.status}</span>
                      {e.attachments?.length > 0 && (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 10, color: '#64748b' }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                          </svg>
                          {e.attachments.length}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════ DRAFTS LIST ═══════════════ */
function DraftsList({ drafts, onOpen, onDelete }) {
  if (drafts.length === 0) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
        <p style={{ fontSize: 14, marginBottom: 8 }}>No drafts</p>
        <p style={{ fontSize: 12 }}>Saved drafts will appear here</p>
      </div>
    );
  }
  return (
    <div style={{ background: '#fff', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
      {drafts.map(d => (
        <div key={d.id} data-testid={`draft-row-${d.id}`}
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fef9c3', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => onOpen(d)}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.to_email || 'No recipient'}
              </span>
              <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0, marginLeft: 8 }}>{timeAgo(d.updated_at || d.created_at)}</span>
            </div>
            <div style={{ fontSize: 13, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.subject || '(no subject)'}
            </div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.body_preview || ''}
            </div>
          </div>
          <button data-testid={`draft-delete-${d.id}`} onClick={(e) => { e.stopPropagation(); onDelete(d.id); }}
            style={{ padding: '4px 8px', background: '#fef2f2', border: 'none', borderRadius: 4, color: '#dc2626', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
            Delete
          </button>
        </div>
      ))}
    </div>
  );
}

/* ═══════════════ EMAIL DETAIL ═══════════════ */
function EmailDetail({ email, onBack }) {
  return (
    <div data-testid="email-detail">
      <button onClick={onBack} data-testid="email-back-btn"
        style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        Back to emails
      </button>
      <div style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,.06)' }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>{email.subject || '(no subject)'}</h2>
        <div style={{ display: 'flex', gap: 20, fontSize: 12, color: '#64748b', marginBottom: 16, flexWrap: 'wrap' }}>
          <span><strong>From:</strong> {email.from_name} &lt;{email.from_email}&gt;</span>
          <span><strong>To:</strong> {email.to_email}</span>
          {email.cc?.length > 0 && <span><strong>CC:</strong> {email.cc.join(', ')}</span>}
          <span><strong>Date:</strong> {formatDate(email.created_at)}</span>
          <span style={{ padding: '1px 6px', borderRadius: 3, fontWeight: 600, fontSize: 10,
            background: email.status === 'sent' ? '#dcfce7' : '#fef2f2',
            color: email.status === 'sent' ? '#16a34a' : '#dc2626',
            textTransform: 'uppercase' }}>{email.status}</span>
        </div>
        {/* Attachments */}
        {email.attachments?.length > 0 && (
          <div data-testid="email-attachments" style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 8px' }}>Attachments ({email.attachments.length})</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {email.attachments.map(att => (
                <AttachmentChip key={att.id} file={att} canRemove={false} />
              ))}
            </div>
          </div>
        )}
        <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
          {email.body_html ? (
            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: email.body_html }} />
          ) : (
            <p style={{ fontSize: 14, color: '#94a3b8' }}>No content</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ ATTACHMENT CHIP ═══════════════ */
const FILE_ICONS = {
  pdf: { bg: '#fef2f2', color: '#dc2626', label: 'PDF' },
  doc: { bg: '#eff6ff', color: '#2563eb', label: 'DOC' },
  docx: { bg: '#eff6ff', color: '#2563eb', label: 'DOC' },
  xls: { bg: '#ecfdf5', color: '#059669', label: 'XLS' },
  xlsx: { bg: '#ecfdf5', color: '#059669', label: 'XLS' },
  png: { bg: '#faf5ff', color: '#7c3aed', label: 'IMG' },
  jpg: { bg: '#faf5ff', color: '#7c3aed', label: 'IMG' },
  jpeg: { bg: '#faf5ff', color: '#7c3aed', label: 'IMG' },
  webp: { bg: '#faf5ff', color: '#7c3aed', label: 'IMG' },
  heic: { bg: '#faf5ff', color: '#7c3aed', label: 'IMG' },
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function AttachmentChip({ file, onRemove, canRemove = true }) {
  const ext = (file.file_type || file.filename?.split('.').pop() || '').toLowerCase();
  const icon = FILE_ICONS[ext] || { bg: '#f1f5f9', color: '#64748b', label: ext.toUpperCase().slice(0, 3) || 'FILE' };
  const isImage = ['png', 'jpg', 'jpeg', 'webp', 'heic', 'gif'].includes(ext);

  return (
    <div data-testid={`attachment-${file.id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12 }}>
      <div style={{ width: 28, height: 28, borderRadius: 6, background: icon.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {isImage ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={icon.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
          </svg>
        ) : (
          <span style={{ fontSize: 8, fontWeight: 800, color: icon.color }}>{icon.label}</span>
        )}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontWeight: 600, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
          {file.original_filename || file.filename}
        </div>
        <div style={{ fontSize: 10, color: '#94a3b8' }}>{formatFileSize(file.file_size)}</div>
      </div>
      {canRemove && onRemove && (
        <button onClick={() => onRemove(file.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      )}
    </div>
  );
}

/* ═══════════════ COMPOSE EMAIL ═══════════════ */
function ComposeEmail({ onSent, onCancel, onDraftSaved, editingDraft, replyTo }) {
  const [to, setTo] = useState(editingDraft?.to_email || replyTo?.from_email || '');
  const [cc, setCc] = useState(editingDraft?.cc?.join(', ') || '');
  const [subject, setSubject] = useState(editingDraft?.subject || (replyTo ? `Re: ${(replyTo.subject || '').replace(/^Re:\s*/i, '')}` : ''));
  const [body, setBody] = useState(editingDraft?.body_text || (replyTo ? `\n\n--- Original Message ---\nFrom: ${replyTo.from_email || ''}\nDate: ${replyTo.received_at || replyTo.created_at || ''}\nSubject: ${replyTo.subject || ''}\n\n${replyTo.body_text || ''}` : ''));
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [showAi, setShowAi] = useState(false);
  const [draftId, setDraftId] = useState(editingDraft?.id || null);
  const [attachments, setAttachments] = useState(editingDraft?.attachments || []);
  const [uploading, setUploading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetch(`${API}/api/sales/email/templates`, { credentials: 'include', headers: getHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(setTemplates)
      .catch(() => {});
  }, []);

  const applyTemplate = (tpl) => {
    // Strip {{variable}} placeholders for manual editing
    const clean = (str) => str.replace(/\{\{[^}]+\}\}/g, '___');
    const stripHtml = (html) => html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/\n{3,}/g, '\n\n').trim();
    setSubject(clean(tpl.subject));
    setBody(stripHtml(clean(tpl.body_html)));
    setShowTemplates(false);
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    const newAttachments = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await fetch(`${API}/api/sales/emails/upload-attachment`, {
          method: 'POST',
          credentials: 'include',
          body: formData,
        });
        if (res.ok) {
          const data = await res.json();
          newAttachments.push(data);
        }
      } catch (err) {
        console.error('Upload failed:', err);
      }
    }
    setAttachments(prev => [...prev, ...newAttachments]);
    setUploading(false);
  };

  const handleFileSelect = (e) => {
    if (e.target.files?.length) uploadFiles(Array.from(e.target.files));
    e.target.value = '';
  };

  const removeAttachment = (fileId) => {
    setAttachments(prev => prev.filter(a => a.id !== fileId));
  };

  const sendEmail = async () => {
    if (!to || !subject) return;
    setSending(true);
    try {
      const bodyHtml = body.split('\n').map(line => line.trim() ? `<p>${line}</p>` : '<br/>').join('');
      const res = await fetch(`${API}/api/sales/emails/compose`, {
        method: 'POST', credentials: 'include', headers: getHeaders(),
        body: JSON.stringify({
          to_email: to.trim(),
          subject,
          body_html: bodyHtml,
          cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
          attachment_file_ids: attachments.map(a => a.id),
          api_base: API,
        }),
      });
      if (res.ok) {
        if (draftId) {
          await fetch(`${API}/api/sales/emails/drafts/${draftId}`, { method: 'DELETE', credentials: 'include', headers: getHeaders() });
        }
        onSent();
      }
    } finally { setSending(false); }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const url = draftId ? `${API}/api/sales/emails/drafts/${draftId}` : `${API}/api/sales/emails/drafts`;
      const method = draftId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method, credentials: 'include', headers: getHeaders(),
        body: JSON.stringify({
          to_email: to.trim(),
          subject,
          body_text: body,
          cc: cc ? cc.split(',').map(e => e.trim()).filter(Boolean) : [],
          attachment_file_ids: attachments.map(a => a.id),
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (!draftId) setDraftId(data.id);
        onDraftSaved();
      }
    } finally { setSaving(false); }
  };

  const generateWithAi = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/api/sales/ai/compose-email`, {
        method: 'POST', credentials: 'include', headers: getHeaders(),
        body: JSON.stringify({
          to_email: to,
          customer_name: to.split('@')[0] || '',
          subject_hint: subject,
          instructions: aiPrompt,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.subject && !subject) setSubject(data.subject);
        setBody(data.body);
        setShowAi(false);
        setAiPrompt('');
      }
    } finally { setAiLoading(false); }
  };

  const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, outline: 'none', boxSizing: 'border-box' };

  return (
    <div data-testid="compose-email" style={{ background: '#fff', borderRadius: 10, padding: '20px 24px', boxShadow: '0 1px 3px rgba(0,0,0,.06)', flex: 1, display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1e293b', margin: 0 }}>{draftId ? 'Edit Draft' : 'New Email'}</h2>
        <button onClick={onCancel} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, width: 50 }}>To:</label>
          <input data-testid="compose-to" value={to} onChange={e => setTo(e.target.value)} placeholder="recipient@email.com" style={inputStyle} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, width: 50 }}>CC:</label>
          <input data-testid="compose-cc" value={cc} onChange={e => setCc(e.target.value)} placeholder="cc@email.com (comma-separated)" style={inputStyle} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <label style={{ fontSize: 12, color: '#64748b', fontWeight: 600, width: 50 }}>Subject:</label>
          <input data-testid="compose-subject" value={subject} onChange={e => setSubject(e.target.value)} placeholder="Email subject" style={inputStyle} />
        </div>
      </div>

      {/* Template Picker */}
      <div style={{ marginBottom: 10, position: 'relative' }}>
        <button data-testid="template-picker-btn" onClick={() => setShowTemplates(!showTemplates)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
            background: showTemplates ? '#1e293b' : '#fff', color: showTemplates ? '#fff' : '#475569',
            border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><path d="M3 9h18M9 21V9"/>
          </svg>
          Use Template
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
        </button>
        {showTemplates && (
          <div data-testid="template-dropdown" style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, background: '#fff',
            border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,.1)', zIndex: 20, width: '100%', maxWidth: 340, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Email Templates</p>
            </div>
            {templates.length === 0 ? (
              <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8', fontSize: 12 }}>No templates available</div>
            ) : (
              templates.map(tpl => (
                <button key={tpl.id} data-testid={`template-${tpl.id}`} onClick={() => applyTemplate(tpl)}
                  style={{ width: '100%', display: 'block', padding: '10px 12px', border: 'none', background: 'transparent',
                    textAlign: 'left', cursor: 'pointer', borderBottom: '1px solid #f8fafc', transition: 'background .1s' }}
                  onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 2 }}>{tpl.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tpl.subject}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* AI Assistant */}
      <div style={{ marginBottom: 10 }}>
        <button data-testid="ai-assist-btn" onClick={() => setShowAi(!showAi)}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: showAi ? '#4338ca' : '#eef2ff', color: showAi ? '#fff' : '#4338ca',
            border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all .15s' }}>
          <span style={{ width: 18, height: 18, background: showAi ? '#fff' : '#4338ca', borderRadius: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: showAi ? '#4338ca' : '#fff', fontSize: 9, fontWeight: 800 }}>AI</span>
          {showAi ? 'Hide AI Writer' : 'AI Write Email'}
        </button>
        {showAi && (
          <div data-testid="ai-assist-panel" style={{ marginTop: 8, padding: 12, background: '#f5f3ff', borderRadius: 8, border: '1px solid #e0e7ff' }}>
            <p style={{ fontSize: 12, color: '#4338ca', margin: '0 0 8px', fontWeight: 500 }}>Tell the AI what to write — it knows NTX's voice and services</p>
            <textarea data-testid="ai-prompt-input" value={aiPrompt} onChange={e => setAiPrompt(e.target.value)}
              placeholder="e.g. Write a follow-up about their pipe fence estimate, mention we can start next week..."
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #c7d2fe', borderRadius: 6, fontSize: 13, resize: 'vertical', minHeight: 60, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
              <button data-testid="ai-generate-btn" onClick={generateWithAi} disabled={aiLoading || !aiPrompt.trim()}
                style={{ padding: '6px 14px', background: '#4338ca', color: '#fff', border: 'none', borderRadius: 5, fontSize: 12, fontWeight: 600, cursor: aiLoading ? 'wait' : 'pointer', opacity: (aiLoading || !aiPrompt.trim()) ? 0.6 : 1 }}>
                {aiLoading ? 'Writing...' : 'Generate Email'}
              </button>
              {['New lead intro', 'Follow up on estimate', 'Schedule site visit', 'Request deposit'].map(q => (
                <button key={q} onClick={() => setAiPrompt(q)}
                  style={{ padding: '4px 10px', background: '#fff', border: '1px solid #c7d2fe', borderRadius: 5, fontSize: 11, color: '#4338ca', cursor: 'pointer' }}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Attachments */}
      <div style={{ marginBottom: 10 }}>
        <input ref={fileInputRef} type="file" multiple onChange={handleFileSelect} style={{ display: 'none' }}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.png,.jpg,.jpeg,.webp,.heic,.gif,.mp4,.mov,.zip" />
        <button data-testid="attach-files-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: '#f1f5f9', color: '#475569',
            border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: uploading ? 'wait' : 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
          </svg>
          {uploading ? 'Uploading...' : 'Attach Files'}
        </button>
        {attachments.length > 0 && (
          <div data-testid="compose-attachments" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            {attachments.map(att => (
              <AttachmentChip key={att.id} file={att} onRemove={removeAttachment} />
            ))}
          </div>
        )}
      </div>

      {/* Email Body */}
      <textarea data-testid="compose-body" value={body} onChange={e => setBody(e.target.value)}
        placeholder="Write your email here..."
        style={{ flex: 1, minHeight: 200, padding: '12px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, lineHeight: 1.6, resize: 'vertical', outline: 'none', fontFamily: 'inherit' }} />

      {/* Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onCancel} style={{ padding: '8px 16px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
            Discard
          </button>
          <button data-testid="save-draft-btn" onClick={saveDraft} disabled={saving}
            style={{ padding: '8px 16px', background: '#fef9c3', color: '#a16207', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 5 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
        </div>
        <button data-testid="send-email-btn" onClick={sendEmail} disabled={sending || !to || !subject}
          style={{ padding: '8px 24px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: (sending || !to || !subject) ? 'not-allowed' : 'pointer', opacity: (sending || !to || !subject) ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
          {sending ? 'Sending...' : 'Send Email'}
        </button>
      </div>
    </div>
  );
}



function InboxList({ emails, onSelect, isMobile }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} data-testid="inbox-list">
      {emails.map(email => (
        <div key={email.id} data-testid={`inbox-email-${email.id}`}
          onClick={() => onSelect(email)}
          style={{
            padding: isMobile ? '12px' : '12px 16px',
            background: email.is_read ? '#fff' : '#f8fafc',
            borderRadius: 8, border: '1px solid #e2e8f0', cursor: 'pointer',
            borderLeft: email.is_read ? '3px solid transparent' : '3px solid #3b82f6',
            transition: 'background .15s',
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span style={{ fontSize: 13, fontWeight: email.is_read ? 500 : 700, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {email.from_name || email.from_email}
                </span>
                {email.match_method && email.match_method !== 'none' && email.match_method !== 'unknown' && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#dcfce7', color: '#166534', fontWeight: 600, flexShrink: 0 }}>
                    {email.match_method === 'customer_email' ? 'CUSTOMER' : email.match_method === 'contact_email' ? 'CONTACT' : email.match_method === 'thread_reply' ? 'REPLY' : 'MATCHED'}
                  </span>
                )}
                {email.match_method === 'unknown' && (
                  <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#fef3c7', color: '#92400e', fontWeight: 600, flexShrink: 0 }}>NEW SENDER</span>
                )}
              </div>
              <p style={{ fontSize: 13, fontWeight: email.is_read ? 400 : 600, color: '#334155', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {email.subject || '(No Subject)'}
              </p>
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {(email.body_text || '').slice(0, 100)}
              </p>
            </div>
            <div style={{ flexShrink: 0, textAlign: 'right' }}>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(email.received_at || email.created_at)}</span>
              {email.attachments?.length > 0 && (
                <p style={{ fontSize: 10, color: '#64748b', margin: '2px 0 0' }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ verticalAlign: 'middle', marginRight: 2 }}>
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
                  </svg>
                  {email.attachments.length}
                </p>
              )}
              {email.is_starred && <span style={{ color: '#f59e0b', fontSize: 14 }}>&#9733;</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function InboxEmailDetail({ email, thread, onBack, onReply, isMobile }) {
  const [linking, setLinking] = useState(false);

  const handleLink = async (type, id) => {
    await fetch(`${API}/api/sales/inbox/${email.id}/link`, {
      method: 'POST', credentials: 'include',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ [type]: id }),
    });
    setLinking(false);
  };

  return (
    <div data-testid="inbox-email-detail">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <button data-testid="inbox-back-btn" onClick={onBack}
          style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12, color: '#475569', cursor: 'pointer', minHeight: 36 }}>
          &larr; Back to Inbox
        </button>
        <div style={{ display: 'flex', gap: 6 }}>
          <button data-testid="inbox-reply-btn" onClick={() => onReply(email)}
            style={{ padding: '8px 16px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', minHeight: 40 }}>
            Reply
          </button>
        </div>
      </div>

      {/* Email Header */}
      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0', padding: isMobile ? '14px' : '20px 24px', marginBottom: 12 }}>
        <h2 style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: '#1e293b', margin: '0 0 12px' }}>{email.subject || '(No Subject)'}</h2>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div>
            <p style={{ fontSize: 13, color: '#1e293b', fontWeight: 600, margin: 0 }}>From: {email.from_name || ''} &lt;{email.from_email}&gt;</p>
            <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>To: {(email.to || []).join(', ')}</p>
            {email.cc?.length > 0 && <p style={{ fontSize: 12, color: '#64748b', margin: '2px 0 0' }}>CC: {email.cc.join(', ')}</p>}
          </div>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>{formatDate(email.received_at || email.created_at)}</span>
        </div>

        {/* Match badge */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {email.match_method && email.match_method !== 'none' && email.match_method !== 'unknown' && (
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#dcfce7', color: '#166534', fontWeight: 600 }}>
              Matched: {email.match_method.replace(/_/g, ' ')}
            </span>
          )}
          {email.match_method === 'unknown' && (
            <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#fef3c7', color: '#92400e', fontWeight: 600 }}>
              Unknown Sender — Not linked to CRM
            </span>
          )}
          {email.customer_id && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#eff6ff', color: '#1e40af' }}>Customer linked</span>}
          {email.account_id && <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: '#f0fdf4', color: '#166534' }}>Account linked</span>}
        </div>

        {/* Attachments */}
        {email.attachments?.length > 0 && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 6 }}>
            <p style={{ fontSize: 11, fontWeight: 600, color: '#475569', margin: '0 0 6px' }}>Attachments ({email.attachments.length})</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {email.attachments.map((att, i) => (
                <span key={i} style={{ fontSize: 11, padding: '4px 8px', background: '#e2e8f0', borderRadius: 4, color: '#475569' }}>
                  {att.filename || 'attachment'} <span style={{ color: '#94a3b8' }}>({att.content_type?.split('/')[1] || 'file'})</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Email Body */}
        <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
          {email.body_html ? (
            <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: email.body_html }} />
          ) : (
            <pre style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>
              {email.body_text || '(No content)'}
            </pre>
          )}
        </div>
      </div>

      {/* Thread */}
      {thread.length > 1 && (
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#1e293b', margin: '0 0 10px' }}>Thread ({thread.length} messages)</h3>
          {thread.filter(t => t.id !== email.id).map(t => (
            <div key={t.id} style={{
              background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px', marginBottom: 8,
              borderLeft: `3px solid ${t.direction === 'inbound' ? '#3b82f6' : '#16a34a'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>
                  {t.direction === 'inbound' ? `From: ${t.from_email || t.from_name}` : `Sent to: ${t.to_email}`}
                </span>
                <span style={{ fontSize: 10, color: '#94a3b8' }}>{timeAgo(t.created_at)}</span>
              </div>
              <p style={{ fontSize: 12, fontWeight: 500, color: '#475569', margin: '0 0 4px' }}>{t.subject || '(No Subject)'}</p>
              <p style={{ fontSize: 12, color: '#64748b', margin: 0, maxHeight: 60, overflow: 'hidden' }}>
                {t.body_text?.slice(0, 200) || ''}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
